import React, { useState, useEffect } from 'react';
import './ScrapedArticlesManager.css';
import { API_CONFIG } from '../../config/api';

interface Article {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  source_url: string;
  source_name: string;
  author: string;
  excerpt: string;
  keywords: string[];
  tags: string[];
  published_at: string;
  scraped_at: string;
  status: string;
  created_at: string;
  updated_at: string;
  brief_summary: string;
  is_highlighted: boolean;
  featured: boolean;
  display_order: number;
  scrape_date: string;
  daily_summary: string;
}

interface ScrapeGroup {
  date: string;
  daily_summary: string;
  articles: Article[];
}

const normalizeDateString = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);

  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }

  // Handle formats like "2025-11-13 00:00:00"
  const sanitized = value.replace(' ', 'T');
  const secondPass = new Date(sanitized);
  return Number.isNaN(secondPass.getTime())
    ? null
    : secondPass.toISOString().split('T')[0];
};

const normalizeScrapeGroup = (scrape: Partial<ScrapeGroup>): ScrapeGroup => {
  const articles = Array.isArray(scrape.articles) ? scrape.articles : [];
  const candidateDate =
    scrape.date ||
    (scrape as any)?.scrape_date ||
    articles[0]?.scrape_date ||
    articles[0]?.scraped_at ||
    articles[0]?.created_at ||
    null;

  const normalizedDate = normalizeDateString(candidateDate) || 'Unknown';

  return {
    date: normalizedDate,
    daily_summary: scrape.daily_summary || articles[0]?.daily_summary || '',
    articles
  };
};

const sortScrapesDesc = (a: ScrapeGroup, b: ScrapeGroup) => {
  if (a.date === 'Unknown') return 1;
  if (b.date === 'Unknown') return -1;
  return a.date < b.date ? 1 : -1;
};

const ScrapedArticlesManager: React.FC = () => {
  const [scrapes, setScrapes] = useState<ScrapeGroup[]>([]);
  const [selectedScrape, setSelectedScrape] = useState<ScrapeGroup | null>(null);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchScrapes();
  }, []);

  const fetchScrapes = async () => {
    try {
      console.log('Fetching scrapes from:', `${API_CONFIG.BASE_URL}/api/admin/scraped-articles`);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/scraped-articles`, {
        cache: 'no-store'
      });
      const data = await response.json();
      console.log('API Response:', data);
      
      if (data.success && Array.isArray(data.scrapes)) {
        // API returns properly structured data - use directly
        setScrapes(data.scrapes);
        console.log('Set scrapes from API response:', data.scrapes.length, 'groups');
        console.log('Scrape dates:', data.scrapes.map((s: any) => s.date));
      } else {
        console.error('Invalid response structure:', data);
        setScrapes([]);
      }
    } catch (error) {
      console.error('Error fetching scrapes:', error);
      setScrapes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchScrapeDetails = async (date: string, options: { showLoader?: boolean } = {}) => {
    const { showLoader = true } = options;
    if (showLoader) {
      setDetailsLoading(true);
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/scraped-articles/${encodeURIComponent(date)}`, {
        cache: 'no-store'
      });
      const data = await response.json();
      if (data.success) {
        const scrapeGroup: ScrapeGroup = {
          date: data.date,
          daily_summary: data.articles[0]?.daily_summary || '',
          articles: data.articles
        };
        setSelectedScrape(scrapeGroup);
      }
    } catch (error) {
      console.error('Error fetching scrape details:', error);
    } finally {
      if (showLoader) {
        setDetailsLoading(false);
      }
    }
  };

  const handleSelectScrape = (scrape: ScrapeGroup) => {
    setSelectedScrape({
      date: scrape.date,
      daily_summary: scrape.daily_summary,
      articles: []
    });
    fetchScrapeDetails(scrape.date, { showLoader: true });
  };

  const updateArticle = async (article: Article) => {
    setSaving(true);
    try {
      // Ensure all required fields are present - preserve original values for fields not being edited
      const articleData = {
        id: article.id,
        title: article.title || '',
        slug: article.slug || '',
        summary: article.summary || '',
        content: article.content || article.summary || '',
        source_url: article.source_url || '',
        source_name: article.source_name || '',
        author: article.author || '',
        excerpt: article.excerpt || article.summary || '',
        keywords: typeof article.keywords === 'string' ? article.keywords : JSON.stringify(article.keywords || []),
        tags: typeof article.tags === 'string' ? article.tags : JSON.stringify(article.tags || []),
        published_at: article.published_at || '',
        scraped_at: article.scraped_at || '',
        status: article.status || 'active',
        created_at: article.created_at || '',
        updated_at: article.updated_at || '',
        brief_summary: article.brief_summary || null,
        is_highlighted: article.is_highlighted ? 1 : 0,
        featured: article.featured ? 1 : 0,
        display_order: article.display_order || 0,
        scrape_date: article.scrape_date || '',
        daily_summary: article.daily_summary || ''
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/scraped-articles/${article.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(articleData)
      });
      
      if (response.ok) {
        // Refresh the scrapes list to show updated data
        await fetchScrapes();
        if (selectedScrape?.date) {
          await fetchScrapeDetails(selectedScrape.date, { showLoader: false });
        }
        setEditingArticle(null);
        alert('Article updated successfully!');
      } else {
        const errorData = await response.json();
        console.error('Update failed:', errorData);
        alert('Failed to update article: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating article:', error);
      alert('Failed to update article: ' + error);
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (articleId: number) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/scraped-articles/${articleId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        // Refresh the selected scrape
        if (selectedScrape?.date) {
          await fetchScrapeDetails(selectedScrape.date, { showLoader: false });
        }
        // Also refresh the main scrapes list
        await fetchScrapes();
      }
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  if (loading) {
    return <div className="scraped-articles-loading">Loading scraped articles...</div>;
  }

  console.log('Rendering component. Scrapes state:', scrapes);
  console.log('Scrapes length in render:', scrapes.length);

  return (
    <div className="scraped-articles-manager">
      <h2>Scraped Articles Manager</h2>
      <p>Debug: Scrapes count = {scrapes.length}</p>
      
      {!selectedScrape ? (
        <div className="scrapes-list">
          <h3>Select a Scrape Date</h3>
          <p>Debug: Found {scrapes.length} scrapes</p>
          {scrapes.length === 0 ? (
            <div>
              <p>No scraped articles found.</p>
              <p>Check browser console for API details.</p>
            </div>
          ) : (
            scrapes.map((scrape) => (
              <div key={scrape.date} className="scrape-item">
                <div className="scrape-header">
                  <h4>{scrape.date}</h4>
                  <span className="article-count">{scrape.articles.length} articles</span>
                </div>
                {scrape.daily_summary && (
                  <p className="daily-summary">{scrape.daily_summary.substring(0, 150)}...</p>
                )}
                <button 
                  onClick={() => handleSelectScrape(scrape)}
                  className="view-scrape-btn"
                >
                  View Articles
                </button>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="scrape-details">
          <div className="scrape-details-header">
            <button 
              onClick={() => setSelectedScrape(null)}
              className="back-btn"
            >
              ← Back to Scrapes
            </button>
            <h3>Articles from {selectedScrape.date}</h3>
          </div>

          {detailsLoading ? (
            <div className="scrape-details-loading">Loading articles...</div>
          ) : (
            <div className="articles-list">
              {selectedScrape.articles.map((article) => (
                <div key={article.id} className="article-item">
                  <div className="article-header">
                    <h4>{article.title}</h4>
                    <div className="article-actions">
                      <button 
                        onClick={() => setEditingArticle(article)}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => deleteArticle(article.id)}
                        className="delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="article-meta">
                    <span>Source: {article.source_name}</span>
                    <span>Status: {article.status}</span>
                    <span>Order: {article.display_order}</span>
                  </div>
                  <p className="article-summary">{article.summary}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editingArticle && (
        <div className="edit-modal">
          <div className="edit-modal-content">
            <h3>Edit Article</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              console.log('Submitting edited article:', editingArticle);
              updateArticle(editingArticle);
            }}>
              <div className="form-group">
                <label>Title:</label>
                <input
                  type="text"
                  value={editingArticle.title}
                  onChange={(e) => {
                    console.log('Title changed to:', e.target.value);
                    setEditingArticle({
                      ...editingArticle,
                      title: e.target.value
                    });
                  }}
                />
              </div>
              
              <div className="form-group">
                <label>Summary:</label>
                <textarea
                  value={editingArticle.summary}
                  onChange={(e) => setEditingArticle({
                    ...editingArticle,
                    summary: e.target.value
                  })}
                  rows={4}
                />
              </div>
              
              <div className="form-group">
                <label>Source Name:</label>
                <input
                  type="text"
                  value={editingArticle.source_name}
                  onChange={(e) => setEditingArticle({
                    ...editingArticle,
                    source_name: e.target.value
                  })}
                />
              </div>
              
              <div className="form-group">
                <label>Author:</label>
                <input
                  type="text"
                  value={editingArticle.author || ''}
                  onChange={(e) => setEditingArticle({
                    ...editingArticle,
                    author: e.target.value
                  })}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Status:</label>
                  <select
                    value={editingArticle.status}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      status: e.target.value
                    })}
                  >
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Display Order:</label>
                  <input
                    type="number"
                    value={editingArticle.display_order}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      display_order: parseInt(e.target.value)
                    })}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <label>
                  <input
                    type="checkbox"
                    checked={editingArticle.is_highlighted}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      is_highlighted: e.target.checked
                    })}
                  />
                  Highlighted
                </label>
                
                <label>
                  <input
                    type="checkbox"
                    checked={editingArticle.featured}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      featured: e.target.checked
                    })}
                  />
                  Featured
                </label>
              </div>
              
              <div className="form-actions">
                <button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setEditingArticle(null)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapedArticlesManager;
